import { RefObject, useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Table.module.css';

import { useAppDispatch } from '../../../hooks/useRedux';
import { useIntersect } from '../../../hooks/useIntersect';

import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { StringByKey } from '../../../types/common';
import { Group, GroupMember, Student } from '../../../types/classroom';

import TableRow from '../TableRow';
import {
  isLargeScreenMediaQuery,
  isMediumScreenMediaQuery,
  isMobile,
  isSmallScreenMediaQuery,
  isXLargeScreenMediaQuery,
  isXXLargeScreenMediaQuery,
} from '../../../utils/util';
import Dropdown from '../../../components/Dropdown';

interface Table {
  name: string;
  headerList: string[];
  headerName: StringByKey;
  list: Student[] | Group[] | GroupMember[];
  updateList: () => void;
  pageChanged?:
    | ActionCreatorWithPayload<any, 'student/studentPageChanged'>
    | ActionCreatorWithPayload<any, 'student/groupPageChanged'>
    | ActionCreatorWithPayload<any, 'student/teacherPageChanged'>;
  parentSeq?: number;
}

export default function Table({
  name,
  headerList,
  headerName,
  list,
  updateList,
  pageChanged,
  parentSeq,
}: Table) {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(0);
  const [isShowColumnDropdown, setIsShowColumnDropdown] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState('email');
  const tableBodyRef = useRef(null);
  const selectColumnRef = useRef(null);

  const dropdownColumns =
    name === 'student'
      ? [
          ...[isMediumScreenMediaQuery ? 'alias' : ''],
          'schoolTypeNm',
          ...[isXLargeScreenMediaQuery ? 'schoolNm' : ''],
          ...[isLargeScreenMediaQuery ? 'gradeTypeNm' : ''],
          'email',
        ].filter((col) => col !== '')
      : [
          ...[isSmallScreenMediaQuery ? 'alias' : ''],
          ...[isXLargeScreenMediaQuery ? 'schoolTypeNm' : ''],
          ...[isMediumScreenMediaQuery ? 'gradeTypeNm' : ''],
          ...[isLargeScreenMediaQuery ? 'subjectTypeNm' : ''],
          'email',
        ].filter((col) => col !== '');

  const intersectRef = useIntersect(
    (entry, observer) => {
      observer.unobserve(entry.target);
      if (!pageChanged) return;

      dispatch(pageChanged(page));
    },
    undefined,
    tableBodyRef
  );

  const changeColumn = (name: string) => {
    setSelectedColumn(name);
  };

  useEffect(() => {
    pageChanged && dispatch(pageChanged(0));
  }, [name]);

  useEffect(() => {
    if (pageChanged) {
      const ITEMS_PER_PAGE = 30;
      setPage(Math.floor(list.length / ITEMS_PER_PAGE));
      if (list.length === ITEMS_PER_PAGE - 1 && page === 1) {
        updateList();
      }
    }
  }, [list]);

  return (
    <>
      <div className={cx('table_list', 'table_head', name)}>
        {headerList.map((header) =>
          isMobile &&
          (name === 'student' || name === 'teacher') &&
          header === selectedColumn ? (
            <span
              key={header}
              className={cx(
                'option_order',
                { show: selectedColumn === header },
                { hide: header === 'email' && selectedColumn !== 'email' }
              )}
            >
              <span className={cx('is_order')}>
                <span className={cx('btn', 'open_order_menu')}>
                  {headerName[header]}
                </span>
                {((name === 'student' && isXXLargeScreenMediaQuery) ||
                  (name === 'teacher' && isXLargeScreenMediaQuery)) && (
                  <span
                    className={cx('btn', 'order_arrow')}
                    ref={selectColumnRef}
                    onClick={() =>
                      setIsShowColumnDropdown(!isShowColumnDropdown)
                    }
                  ></span>
                )}
                <Dropdown
                  name="columnSelection"
                  classnames={['type_order_menu', 'list']}
                  list={dropdownColumns}
                  clickHandlers={[changeColumn]}
                  isOpen={isShowColumnDropdown}
                  button={selectColumnRef}
                  backdropHandler={() => setIsShowColumnDropdown(false)}
                />
              </span>
            </span>
          ) : (
            <span
              key={header}
              className={cx(
                { show: selectedColumn === header },
                { hide: header === 'email' && selectedColumn !== 'email' }
              )}
            >
              {headerName[header]}
            </span>
          )
        )}
      </div>
      <div className={cx('table_list', 'table_body', name)} ref={tableBodyRef}>
        {list.map((item) => (
          <TableRow
            key={item.seq}
            name={name}
            list={headerList}
            item={item}
            ref={intersectRef as RefObject<HTMLDivElement>}
            parentSeq={parentSeq}
            selectedColumn={selectedColumn}
          />
        ))}
      </div>
    </>
  );
}
